import { useRef, useState, useCallback, useEffect } from "react";
import styled from "@emotion/styled";
import { motion, AnimatePresence } from "motion/react";
import { IMDSSessionProps, SessionContextMention } from "@/mds/components/sessions/session.types";
import { MDSCombobox, MDSComboboxVariant, MDSComboboxSubmitHandler } from "@/mds/components/forms";
import { MemCommonRichTextInputInstance } from "@mem-labs/common-editor";
import { useMDSSession } from "@/mds/components/sessions/session-context/mds-session-context";
import { extractCollectionMentions, extractNoteMentions } from "./session-content.utils";
import { MDSSessionList } from "../session-list";
import { MDSBanner } from "@/mds/components/banner/banner";
import { MDSBannerKind, MDSBannerSlotName } from "@/mds/components/banner/banner.types";
import { useSessionFeature } from "../session-context";
import { SessionFeature, SessionStatus } from "../session.types";
import { MdsIconKind } from "@/design-system/components/icon";
import { MDSButton, MDSButtonVariant } from "@/mds";

// Fallback height when dynamic height hasn't been measured yet
const defaultComboboxHeight = 58;

export const MDSSessionContentStatic = ({
  handlers: { handleFetchComboboxData, handleInputFormSubmit, handleNewSession },
  comboboxProps,
  isOpen = true,
}: IMDSSessionProps) => {
  const {
    sessionViewMode,
    handleInputFocus,
    handleInputBlur,
    handleInputChange,
    sessionStatus,
    activeArtifact,
    sessionConfig,
    finalizedSessionItems,
    internalSessionEvents,
    setComboboxInputRef,
  } = useMDSSession();

  // Derive sessionId from the first session event
  const sessionId = internalSessionEvents[0]?.session_id || "";

  const isNewChatButtonEnabled = useSessionFeature(
    SessionFeature.ContextLimitExceededNewChatButton
  );

  const comboboxInputRef = useRef<MemCommonRichTextInputInstance>(null);
  const comboboxContainerRef = useRef<HTMLFormElement>(null);
  const bannerRef = useRef<HTMLElement>(null);
  const [comboboxHeight, setComboboxHeight] = useState(defaultComboboxHeight);
  const [bannerHeight, setBannerHeight] = useState(0);

  // Set the combobox input ref in the session context when the component mounts and updates
  useEffect(() => {
    setComboboxInputRef(comboboxInputRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comboboxInputRef.current]);

  // Track banner height when visible
  const updateBannerHeight = useCallback(() => {
    if (bannerRef.current) {
      const newHeight = bannerRef.current.offsetHeight;
      if (newHeight !== bannerHeight) {
        setBannerHeight(newHeight);
      }
    } else if (bannerHeight !== 0) {
      setBannerHeight(0);
    }
  }, [bannerHeight]);

  useEffect(() => {
    updateBannerHeight();
  });

  useEffect(() => {
    let bannerObserver: ResizeObserver | null = null;
    if (bannerRef.current) {
      bannerObserver = new ResizeObserver(() => updateBannerHeight());
      bannerObserver.observe(bannerRef.current);
    }

    return () => {
      if (bannerObserver) bannerObserver.disconnect();
    };
  }, [updateBannerHeight]);

  // Custom input change handler that includes height tracking
  const handleInputChangeWithHeight = useCallback(
    (height?: number) => {
      // Call the original input change handler
      handleInputChange();

      // record the new height
      if (
        typeof height === "number" &&
        height >= defaultComboboxHeight &&
        height !== comboboxHeight
      ) {
        setComboboxHeight(height);
      }
    },
    [comboboxHeight, handleInputChange]
  );

  const handleComboboxSubmit: MDSComboboxSubmitHandler = ({
    mdxContent,
    mentions,
    implicitMentions,
  }) => {
    // TODO - Clean this up.
    const mentionedCollections = extractCollectionMentions(mentions);
    const mentionedNotes = extractNoteMentions(mentions);

    let extractedImplicitMentions: SessionContextMention[] | undefined;
    if (implicitMentions) {
      const implicitMentionedNoted = extractNoteMentions(implicitMentions);
      const implicitMentionedCollections = extractCollectionMentions(implicitMentions);

      extractedImplicitMentions = [...implicitMentionedCollections, ...implicitMentionedNoted];
    }

    return handleInputFormSubmit({
      content: mdxContent,
      context: {
        // TODO - Clean this up.
        mentions: [...mentionedCollections, ...mentionedNotes],
        implicit_mentions: extractedImplicitMentions,
      },
    });
  };

  const isBannerVisible = sessionStatus === SessionStatus.ContextLimitExceeded;
  const listHeight = `calc(100dvh - 176px - ${comboboxHeight}px${isBannerVisible ? ` - ${bannerHeight}px` : ""})`;

  return (
    <AnimatePresence mode="wait">
      <ContentContainer data-component="ContentContainer">
        <AnimatedContainer>
          {isBannerVisible && (
            <MDSBanner
              kind={MDSBannerKind.Error}
              iconKind={MdsIconKind.InfoCircle}
              message="This chat has gotten too long"
              ref={bannerRef}
            >
              {isNewChatButtonEnabled && (
                <MDSButton
                  variant={MDSButtonVariant.Danger}
                  slot={MDSBannerSlotName.Right}
                  onClick={handleNewSession}
                >
                  New chat
                </MDSButton>
              )}
            </MDSBanner>
          )}

          {isOpen && (
            <StyledListContainer
              data-component="StyledListContainer"
              initial={{ opacity: 0 }}
              animate={{ opacity: isOpen ? 1 : 0 }}
              transition={{ duration: 0.8, delay: 0.5, ease: [0, 0.71, 0.2, 1.01] }}
              exit={{ opacity: 0 }}
            >
              <MDSSessionList
                sessionItems={finalizedSessionItems}
                sessionId={sessionId}
                sessionStatus={sessionStatus}
                activeArtifact={activeArtifact}
                containerHeight={listHeight}
                sessionConfig={sessionConfig}
                sessionViewMode={sessionViewMode}
                disableTopFade
              />
            </StyledListContainer>
          )}

          <StyledSection data-component="StyledSection">
            <StyledForm
              ref={comboboxContainerRef}
              onSubmit={e => {
                e.preventDefault();
              }}
            >
              <MDSCombobox
                placeholder="Anything else?"
                fetchData={handleFetchComboboxData}
                variant={MDSComboboxVariant.Brand}
                onSubmit={handleComboboxSubmit}
                popoverProps={{
                  shouldFlip: true,
                }}
                onInputFocus={handleInputFocus}
                onInputBlur={handleInputBlur}
                onInputChange={handleInputChangeWithHeight}
                autoFocus={false}
                inputRef={comboboxInputRef}
                mentions={[]}
                {...comboboxProps}
              />
            </StyledForm>
          </StyledSection>
        </AnimatedContainer>
      </ContentContainer>
    </AnimatePresence>
  );
};

const ContentContainer = styled.div`
  height: 100%;
  min-width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const AnimatedContainer = styled(motion.div)`
  height: 100%;
  width: 100%;
  will-change: height;
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
  position: relative;

  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  overflow: hidden;
`;

const StyledSection = styled.section`
  width: 100%;
  padding-left: ${({ theme }) => `${theme.spacing.lg}`};
  padding-right: ${({ theme }) => `${theme.spacing.lg}`};
  padding-bottom: ${({ theme }) => `${theme.spacing.lg}`};
  padding-top: 8px;
  min-height: 60px;

  position: absolute;
  bottom: 0;
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin-bottom: 0;
`;

export const StyledListContainer = styled(motion.div)`
  width: 100%;
  display: flex;
  flex-direction: column;
  text-align: left;
`;
